import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';

const getFromTo = (dateRange, customStart, customEnd) => {
  const now = new Date();
  let from = new Date();
  let to = new Date();
  if (dateRange === '7d') {
    from.setDate(now.getDate() - 6);
  } else if (dateRange === '30d') {
    from.setDate(now.getDate() - 29);
  } else if (dateRange === 'custom' && customStart && customEnd) {
    from = new Date(customStart);
    to = new Date(customEnd);
  } else {
    return null;
  }
  from.setHours(0, 0, 0, 0);
  to.setHours(23, 59, 59, 999);
  const fmt = (d) => d.toISOString().split('.')[0];
  return { from: fmt(from), to: fmt(to) };
};

const f2 = (v) => parseFloat(Number(v).toFixed(2));

const InlineStackedBar = ({ promoters, passives, detractors }) => (
  <div className="flex h-2 rounded-full overflow-hidden w-full min-w-[80px]">
    <div className="h-full bg-green-500 transition-all duration-500" style={{ width: `${promoters}%` }} title={`Promoters: ${promoters}%`} />
    <div className="h-full bg-yellow-400 transition-all duration-500" style={{ width: `${passives}%` }} title={`Passives: ${passives}%`} />
    <div className="h-full bg-red-500 transition-all duration-500" style={{ width: `${detractors}%` }} title={`Detractors: ${detractors}%`} />
  </div>
);

const NPSQuestionTable = () => {
  const [sortBy, setSortBy] = useState('detractors');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [dateFilter, setDateFilter] = useState('30d');
  const [hoveredPct, setHoveredPct] = useState(null);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [appliedCustomRange, setAppliedCustomRange] = useState(null);
  const [activeLabel, setActiveLabel] = useState('Last 30 days');
  const [questionsData, setQuestionsData] = useState([]);
  const [loading, setLoading] = useState(true);

  const sortOptions = [
    { value: 'detractors', label: 'Highest Detractors' },
    { value: 'responses', label: 'Highest Responses' },
    { value: 'promoters_low', label: 'Lowest Promoters' },
  ];

  const dateOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: 'custom', label: 'Custom' },
  ];

  const fetchQuestions = () => {
    const range = getFromTo(dateFilter, customStart, customEnd);
    if (!range) return;
    setLoading(true);
    api.get('/api/v1/nps-analytics/questions', {
      params: { ...range, sortBy }
    })
      .then((res) => {
        const rows = (res.data?.data?.questions || res.data?.questions || []).map((q, idx) => ({
          id: idx + 1,
          text: q.dimension,
          respondents: q.respondents,
          promoters: q.promotersPercent,
          passives: q.passivesPercent,
          detractors: q.detractorsPercent,
          dropOff: q.dropOffPercent,
          avgRating: q.avgRating,
          sampleResponses: (q.sampleResponses || []).map((s, si) => ({
            id: si + 1,
            score: s.score,
            text: s.text,
            sentiment: s.sentiment,
          })),
        }));
        setQuestionsData(rows);
      })
      .catch((err) => console.error('Failed to fetch NPS questions:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchQuestions();
  }, [dateFilter, customStart, customEnd, sortBy]);

  const sortedData = [...questionsData]?.sort((a, b) => {
    if (sortBy === 'detractors') return b?.detractors - a?.detractors;
    if (sortBy === 'responses') return b?.respondents - a?.respondents;
    if (sortBy === 'promoters_low') return a?.promoters - b?.promoters;
    return 0;
  });

  const getRowTint = (row) => {
    if (row?.detractors >= 35) return 'bg-red-50 hover:bg-red-100';
    if (row?.promoters >= 65) return 'bg-green-50 hover:bg-green-100';
    return 'hover:bg-muted/50';
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    if (value !== 'custom') {
      setAppliedCustomRange(null);
      setCustomStart('');
      setCustomEnd('');
      setActiveLabel(value === '7d' ? 'Last 7 days' : 'Last 30 days');
    }
  };

  const handleApplyCustomRange = () => {
    if (customStart && customEnd) {
      setAppliedCustomRange({ start: customStart, end: customEnd });
      setActiveLabel(`${customStart} → ${customEnd}`);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-foreground">Question-Level Details</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Click any row to view individual responses and transcripts</p>
            {(dateFilter !== 'custom' || appliedCustomRange) && (
              <p className="text-xs text-primary mt-1 font-medium">
                Showing data for: {activeLabel}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Filter */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                {dateOptions?.map((opt) => (
                  <button
                    key={opt?.value}
                    onClick={() => handleDateFilterChange(opt?.value)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                      dateFilter === opt?.value
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {opt?.label}
                  </button>
                ))}
              </div>
              {dateFilter === 'custom' && (
                <div className="flex items-center gap-2 bg-muted/60 border border-border rounded-lg px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-muted-foreground whitespace-nowrap">From</label>
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e?.target?.value)}
                      max={customEnd || undefined}
                      className="text-xs border border-border rounded-md px-2 py-1 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-muted-foreground whitespace-nowrap">To</label>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e?.target?.value)}
                      min={customStart || undefined}
                      className="text-xs border border-border rounded-md px-2 py-1 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <button
                    onClick={handleApplyCustomRange}
                    disabled={!customStart || !customEnd}
                    className="text-xs font-medium px-3 py-1 rounded-md bg-primary text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
                  >
                    Apply
                  </button>
                </div>
              )}
              {dateFilter === 'custom' && appliedCustomRange && (
                <div className="flex items-center gap-1.5 px-1">
                  <span className="text-xs text-green-600 font-medium">✓ Range applied: {appliedCustomRange?.start} → {appliedCustomRange?.end}</span>
                </div>
              )}
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="text-xs border border-border rounded-lg px-3 py-2 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              {sortOptions?.map((opt) => (
                <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : questionsData.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">No question data available for this period.</div>
      ) : (
        <>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-[35%]">Question</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Respondents</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-green-600 uppercase tracking-wider">Promoters</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-yellow-600 uppercase tracking-wider">Passives</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-red-600 uppercase tracking-wider">Detractors</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-[120px]">Distribution</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Drop-off</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sortedData?.map((row) => (
                  <tr
                    key={row?.id}
                    className={`cursor-pointer transition-colors duration-150 ${getRowTint(row)}`}
                    onClick={() => setSelectedQuestion(selectedQuestion?.id === row?.id ? null : row)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-2">
                        <span className="text-xs font-bold text-muted-foreground mt-0.5 w-4 flex-shrink-0">Q{row?.id}</span>
                        <span className="text-sm text-foreground leading-snug">{row?.text}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="text-sm font-medium data-text text-foreground">{row?.respondents?.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span
                        className="text-sm font-semibold data-text text-green-600 cursor-help"
                        title={`${Math.round(row?.respondents * row?.promoters / 100)} respondents`}
                      >
                        {f2(row?.promoters)}%
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span
                        className="text-sm font-semibold data-text text-yellow-600 cursor-help"
                        title={`${Math.round(row?.respondents * row?.passives / 100)} respondents`}
                      >
                        {f2(row?.passives)}%
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span
                        className="text-sm font-semibold data-text text-red-600 cursor-help"
                        title={`${Math.round(row?.respondents * row?.detractors / 100)} respondents`}
                      >
                        {f2(row?.detractors)}%
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <InlineStackedBar promoters={row?.promoters} passives={row?.passives} detractors={row?.detractors} />
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className={`text-xs font-medium data-text ${row?.dropOff > 10 ? 'text-red-600' : 'text-muted-foreground'}`}>
                        {f2(row?.dropOff)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Drill-down Panel */}
          {selectedQuestion && (
            <div className="border-t border-border bg-muted/30 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-muted-foreground">Q{selectedQuestion?.id}</span>
                    <h4 className="text-sm font-semibold text-foreground">{selectedQuestion?.text}</h4>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>{selectedQuestion?.respondents?.toLocaleString()} total respondents</span>
                    <span>Avg rating: <span className="font-medium text-foreground data-text">{f2(selectedQuestion?.avgRating)}</span></span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedQuestion(null)}
                  className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center hover:bg-border transition-colors"
                >
                  <Icon name="X" size={14} color="#6B7280" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(selectedQuestion?.sampleResponses?.length > 0 ? selectedQuestion.sampleResponses : [
                  { id: 1, score: 9, text: 'Very satisfied with the experience.', sentiment: 'promoter' },
                  { id: 2, score: 7, text: 'Decent service, could be improved.', sentiment: 'passive' },
                  { id: 3, score: 3, text: 'Not happy with the outcome.', sentiment: 'detractor' },
                ])?.map((resp) => (
                  <div
                    key={resp?.id}
                    className={`p-4 rounded-xl border ${
                      resp?.sentiment === 'promoter' ? 'bg-green-50 border-green-200' :
                      resp?.sentiment === 'passive'? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-semibold uppercase tracking-wider ${
                        resp?.sentiment === 'promoter' ? 'text-green-700' :
                        resp?.sentiment === 'passive'? 'text-yellow-700' : 'text-red-700'
                      }`}>{resp?.sentiment}</span>
                      <span className={`text-lg font-bold data-text ${
                        resp?.sentiment === 'promoter' ? 'text-green-700' :
                        resp?.sentiment === 'passive'? 'text-yellow-700' : 'text-red-700'
                      }`}>{resp?.score}/10</span>
                    </div>
                    <p className="text-sm text-foreground leading-snug">"{resp?.text}"</p>
                  </div>
                ))}
              </div>

              {selectedQuestion?.detractors >= 35 && (
                <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                  <Icon name="AlertTriangle" size={14} color="#DC2626" />
                  <p className="text-xs text-red-800">
                    <span className="font-semibold">High detractor alert:</span> {f2(selectedQuestion?.detractors)}% detractors on this question — consider reviewing the call script for this topic.
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NPSQuestionTable;
